import { useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface MatchingPairsQuestionProps {
  question: string;
  pairs: [string, string][]; // [left, right] correct pairings
  onAnswer: (correct: boolean) => void;
  answered: boolean;
}

export function MatchingPairsQuestion({
  question,
  pairs,
  onAnswer,
  answered: externalAnswered,
}: MatchingPairsQuestionProps) {
  // Shuffle left and right sides independently
  const { leftItems, rightItems, correctMap } = useMemo(() => {
    const left = pairs.map((p, i) => ({ label: p[0], id: i }));
    const right = pairs.map((p, i) => ({ label: p[1], id: i }));
    // Fisher-Yates shuffle
    const shuffle = <T,>(arr: T[]): T[] => {
      const a = [...arr];
      for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
      }
      return a;
    };
    const map: Record<number, number> = {};
    pairs.forEach((_, i) => { map[i] = i; });
    return { leftItems: shuffle(left), rightItems: shuffle(right), correctMap: map };
  }, [pairs]);

  const [selectedLeft, setSelectedLeft] = useState<number | null>(null);
  const [matches, setMatches] = useState<Record<number, number>>({}); // leftId -> rightId
  const [wrongPair, setWrongPair] = useState<[number, number] | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const matchedLeftIds = new Set(Object.keys(matches).map(Number));
  const matchedRightIds = new Set(Object.values(matches));

  const handleLeftClick = useCallback((id: number) => {
    if (submitted || matchedLeftIds.has(id)) return;
    setSelectedLeft(id);
    setWrongPair(null);
  }, [submitted, matchedLeftIds]);

  const handleRightClick = useCallback((id: number) => {
    if (submitted || selectedLeft === null || matchedRightIds.has(id)) return;
    
    if (correctMap[selectedLeft] === id) {
      // Correct match
      const newMatches = { ...matches, [selectedLeft]: id };
      setMatches(newMatches);
      setSelectedLeft(null);
      setWrongPair(null);

      // Check if all matched
      if (Object.keys(newMatches).length === pairs.length) {
        setSubmitted(true);
        onAnswer(true);
      }
    } else {
      // Wrong match — flash and clear
      setWrongPair([selectedLeft, id]);
      setTimeout(() => {
        setWrongPair(null);
        setSelectedLeft(null);
      }, 600);
      // Only report wrong on final check; here just flash
    }
  }, [submitted, selectedLeft, correctMap, matches, matchedRightIds, pairs.length, onAnswer]);

  // If taking too long (all wrong), let them know
  const handleGiveUp = useCallback(() => {
    if (submitted) return;
    setSubmitted(true);
    // Fill all correct matches for display
    const allMatches: Record<number, number> = {};
    pairs.forEach((_, i) => { allMatches[i] = i; });
    setMatches(allMatches);
    onAnswer(false);
  }, [submitted, pairs, onAnswer]);

  return (
    <div className="w-full space-y-5">
      <div className="text-center">
        <span className="inline-block bg-violet-500/15 text-violet-600 dark:text-violet-400 font-display font-bold text-xs px-3 py-1 rounded-full mb-3">
          🔗 MATCH PAIRS
        </span>
      </div>

      <p className="font-body font-semibold text-base text-center leading-snug">
        {question}
      </p>

      <div className="grid grid-cols-2 gap-3">
        {/* Left column */}
        <div className="space-y-2">
          {leftItems.map((item) => {
            const isMatched = matchedLeftIds.has(item.id);
            const isSelected = selectedLeft === item.id;
            const isWrong = wrongPair?.[0] === item.id;

            let cls = 'bg-card border-border';
            if (isMatched) cls = 'bg-success/10 border-success/50';
            else if (isWrong) cls = 'bg-destructive/10 border-destructive animate-pulse';
            else if (isSelected) cls = 'bg-primary/10 border-primary ring-2 ring-primary/30';

            return (
              <motion.button
                key={`l-${item.id}`}
                onClick={() => handleLeftClick(item.id)}
                disabled={isMatched || submitted}
                whileTap={!isMatched ? { scale: 0.97 } : {}}
                className={`w-full p-3 rounded-xl border-2 text-sm font-body font-medium text-left transition-colors ${cls}`}
              >
                {item.label}
                {isMatched && <span className="ml-1">✅</span>}
              </motion.button>
            );
          })}
        </div>

        {/* Right column */}
        <div className="space-y-2">
          {rightItems.map((item) => {
            const isMatched = matchedRightIds.has(item.id);
            const isWrong = wrongPair?.[1] === item.id;

            let cls = 'bg-secondary text-secondary-foreground border-border';
            if (isMatched) cls = 'bg-success/10 border-success/50';
            else if (isWrong) cls = 'bg-destructive/10 border-destructive animate-pulse';

            return (
              <motion.button
                key={`r-${item.id}`}
                onClick={() => handleRightClick(item.id)}
                disabled={isMatched || submitted}
                whileTap={!isMatched ? { scale: 0.97 } : {}}
                className={`w-full p-3 rounded-xl border-2 text-sm font-body font-medium text-left transition-colors ${cls}`}
              >
                {item.label}
                {isMatched && <span className="ml-1">✅</span>}
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Hint: tap left first, then right */}
      {!submitted && Object.keys(matches).length === 0 && (
        <p className="text-center text-xs text-muted-foreground">
          Tap a term on the left, then its match on the right
        </p>
      )}

      {/* Give up button after some attempts */}
      {!submitted && Object.keys(matches).length < pairs.length && Object.keys(matches).length > 0 && (
        <button
          onClick={handleGiveUp}
          className="w-full text-center text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground transition-colors"
        >
          Show answers
        </button>
      )}
    </div>
  );
}
